import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  X,
  Flag,
  ChevronDown,
  Users,
  Layers,
} from "lucide-react";
import { Badge } from "../ui/Badge";
import { templatePath } from "../Templates/templateUrls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/Select";
import { normalizeProviderName } from "./buildGraph";
import {
  collectAllProviders,
  buildRouteRows,
  findWinnersBySpecialty,
} from "./buildTable";

// Radix Select forbids an empty-string item value, so "no visit type picked"
// travels as a sentinel and maps back to null at the state boundary.
const NO_VISIT_TYPE = "__none__";

// Distinct from NO_VISIT_TYPE: this is a real visit whose type no route lists,
// which is how you see the fall-through to untyped routes and the fallback.
const ANY_VISIT_TYPE = "__any__";

// Mirrors RESOURCE_STYLES in nodes.js so a template reads the same in both
// views.
const RESOURCE_STYLES = {
  soap_note_template: {
    label: "NOTE",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  conversation_prompt: {
    label: "CONV",
    className: "border-green-200 bg-green-50 text-green-700",
  },
  secondary_prompt: {
    label: "2ND",
    className: "border-purple-200 bg-purple-50 text-purple-700",
  },
};

const HEAD_CELL =
  "border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500";

// Gutters separating the condition columns from each other and from the
// templates they resolve to, which otherwise blur into one another.
const FLAG_GUTTER = "pl-10";
const RESOURCE_GUTTER = "pl-8";

const CHIP_TONES = {
  // One step brighter than the muted purple scale, still well under the
  // resource-link ktx-purple. Selected is one fill deeper so it stands out.
  doctorSelected: "border-purple-400 bg-purple-200 text-purple-900 font-semibold",
  doctor: "border-purple-300 bg-purple-100 text-purple-800",
  value: "border-gray-200 bg-gray-100 text-gray-800",
  flag: "border-orange-200 bg-orange-50 text-orange-800",
  // An absent constraint reads quieter than a value, so "Any doctor" never
  // looks like missing data.
  none: "border-gray-200 bg-gray-50 italic text-gray-500",
};

function ScopeChip({ tone = "none", children }) {
  return (
    <Badge
      variant="outline"
      className={`whitespace-nowrap px-2.5 py-1 text-sm font-medium ${CHIP_TONES[tone]}`}
    >
      {children}
    </Badge>
  );
}

function DoctorsCell({ providers, isDefault, selectedProviderName }) {
  // The fallback route has no conditions, so it reads like an any-doctor route;
  // the FALLBACK badge is what marks it as last in line.
  if (isDefault || !providers?.length) return <ScopeChip>Any doctor</ScopeChip>;
  const selectedKey = normalizeProviderName(selectedProviderName);
  return (
    <div className="flex flex-wrap gap-1.5">
      {providers.map((p) => (
        <ScopeChip
          key={p.id}
          tone={
            selectedKey && normalizeProviderName(p.name) === selectedKey
              ? "doctorSelected"
              : "doctor"
          }
        >
          {p.name}
        </ScopeChip>
      ))}
    </div>
  );
}

function VisitTypeCell({ types, isDefault }) {
  if (isDefault || !types?.length) return <ScopeChip>Any visit type</ScopeChip>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {types.map((t) => (
        <ScopeChip key={t} tone="value">
          {t}
        </ScopeChip>
      ))}
    </div>
  );
}

function FlagsCell({ flags, isDefault }) {
  if (isDefault || !flags?.length) return <ScopeChip>No flag</ScopeChip>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map((f) => (
        <ScopeChip key={f} tone="flag">
          {f}
        </ScopeChip>
      ))}
    </div>
  );
}

function ResourcesCell({ resources }) {
  if (!resources?.length) {
    return <span className="text-sm italic text-gray-400">No resources</span>;
  }
  return (
    <div className="space-y-1.5">
      {resources.map(({ key, id }) => {
        const meta = RESOURCE_STYLES[key] || {
          label: key,
          className: "border-gray-200 bg-gray-50 text-gray-600",
        };
        return (
          <div key={key} className="flex min-w-0 items-start gap-2">
            <Badge
              variant="outline"
              className={`mt-px shrink-0 rounded-md px-1.5 py-0.5 text-xs font-semibold ${meta.className}`}
            >
              {meta.label}
            </Badge>
            {/* Ids are long, so they wrap rather than truncate. Templates reads
                the id as its search term. */}
            <Link
              to={templatePath(id)}
              className="min-w-0 break-words text-sm text-ktx-purple underline decoration-ktx-purple/40 underline-offset-2 transition-colors hover:decoration-ktx-purple"
              title={`Find ${id} in Templates`}
            >
              {id}
            </Link>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Doctor-first table: routes for one doctor, in backend evaluation order. The
 * doctor comes from the URL so a table can be linked to; specialty, visit type,
 * and flags stay local.
 */
function TableView({
  workflowsList,
  loading,
  loadError,
  selectedProviderId: selectedProvider,
  selectedSpecialty,
  onSelectProvider,
  onSelectSpecialty,
  doctorId,
}) {
  const [providerSearch, setProviderSearch] = useState("");
  const [providerOpen, setProviderOpen] = useState(false);
  const providerRef = useRef(null);
  const providerInputRef = useRef(null);

  const [selectedVisitType, setSelectedVisitType] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState(() => new Set());
  const [flagsOpen, setFlagsOpen] = useState(false);
  const flagsRef = useRef(null);

  // Width the rows lose to their scrollbar, mirrored as header padding so the
  // columns line up. Zero on overlay scrollbars.
  const bodyRef = useRef(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const allProviders = useMemo(
    () => collectAllProviders(workflowsList),
    [workflowsList]
  );

  const selectedProviderEntry = useMemo(
    () => allProviders.find((p) => p.id === selectedProvider) || null,
    [allProviders, selectedProvider]
  );

  const specialtyOptions = selectedProviderEntry?.specialties || [];
  const needsSpecialtyChoice = specialtyOptions.length > 1;

  const specialtyWorkflow = useMemo(
    () =>
      selectedSpecialty
        ? (workflowsList || []).find((w) => w.specialty === selectedSpecialty)
        : null,
    [workflowsList, selectedSpecialty]
  );

  const { rows } = useMemo(() => {
    if (!selectedProvider || !selectedSpecialty) return { rows: [] };
    return buildRouteRows(workflowsList, {
      providerId: selectedProvider,
      providerName: selectedProviderEntry?.name || null,
      specialty: selectedSpecialty,
    });
  }, [
    workflowsList,
    selectedProvider,
    selectedProviderEntry,
    selectedSpecialty,
  ]);

  // Only conditions some visible route asks for: any other behaves exactly like
  // "Any visit type", so naming it would suggest a distinction that isn't there.
  const visitTypes = useMemo(() => {
    const set = new Set();
    for (const row of rows) {
      for (const type of row.types || []) set.add(type);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const flags = useMemo(() => {
    const set = new Set();
    for (const row of rows) {
      for (const flag of row.flags || []) set.add(flag);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const showFlagsColumn = flags.length > 0;

  const filteredProviders = useMemo(() => {
    const query = providerSearch.trim().toLowerCase();
    if (!query) return allProviders;
    return allProviders.filter((p) => p.name.toLowerCase().includes(query));
  }, [allProviders, providerSearch]);

  useEffect(() => {
    if (!providerOpen) return undefined;
    const onDown = (e) => {
      if (providerRef.current && !providerRef.current.contains(e.target)) {
        setProviderOpen(false);
        setProviderSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [providerOpen]);

  useEffect(() => {
    if (!flagsOpen) return undefined;
    const onDown = (e) => {
      if (flagsRef.current && !flagsRef.current.contains(e.target)) {
        setFlagsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [flagsOpen]);

  // Drop conditions no visible route asks for. "Any" survives, since it is
  // never one of the listed types.
  useEffect(() => {
    if (
      selectedVisitType &&
      selectedVisitType !== ANY_VISIT_TYPE &&
      !visitTypes.includes(selectedVisitType)
    ) {
      setSelectedVisitType(null);
    }
    setSelectedFlags((prev) => {
      const next = new Set([...prev].filter((f) => flags.includes(f)));
      return next.size === prev.size ? prev : next;
    });
  }, [visitTypes, flags, selectedVisitType]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setScrollbarWidth(el.offsetWidth - el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows]);

  // Conditions describe one doctor in one specialty, so they reset when either
  // changes — including via back/forward, which bypasses the pickers.
  useEffect(() => {
    setSelectedVisitType(null);
    setSelectedFlags(new Set());
  }, [selectedProvider, selectedSpecialty]);

  const conditionsActive = Boolean(selectedVisitType) || selectedFlags.size > 0;

  // "Any" reaches the matcher as an unrestricted patient type, leaving only
  // untyped routes eligible, but must stay distinct from "nothing picked yet".
  const anyPatientType = selectedVisitType === ANY_VISIT_TYPE;
  const matchPatientType = anyPatientType ? null : selectedVisitType;

  const winnersBySpecialty = useMemo(
    () =>
      findWinnersBySpecialty(specialtyWorkflow ? [specialtyWorkflow] : [], {
        providerId: selectedProvider,
        providerName: selectedProviderEntry?.name || null,
        patientType: matchPatientType,
        anyPatientType,
        flags: [...selectedFlags],
      }),
    [
      specialtyWorkflow,
      selectedProvider,
      selectedProviderEntry,
      matchPatientType,
      anyPatientType,
      selectedFlags,
    ]
  );

  const handleSelectProvider = useCallback(
    (provider) => {
      setProviderOpen(false);
      setProviderSearch("");
      onSelectProvider?.(provider);
    },
    [onSelectProvider]
  );

  const openProviderSearch = useCallback(() => {
    setProviderOpen(true);
    setProviderSearch("");
    providerInputRef.current?.focus();
  }, []);

  const toggleFlag = useCallback((flag) => {
    setSelectedFlags((prev) => {
      const next = new Set(prev);
      if (next.has(flag)) next.delete(flag);
      else next.add(flag);
      return next;
    });
  }, []);

  // Clears the condition pickers only: the table is meaningless with no doctor.
  const clearConditions = () => {
    setSelectedVisitType(null);
    setSelectedFlags(new Set());
  };

  // Shared by the header and body tables; identical widths keep them aligned.
  const renderColumns = () => (
    <colgroup>
      <col className="w-28" />
      <col className="w-[20%]" />
      <col className="w-48" />
      {showFlagsColumn && <col className="w-60" />}
      <col />
    </colgroup>
  );

  const isRowMatched = (row) => {
    if (!selectedProvider || !conditionsActive) return false;
    const winner = winnersBySpecialty.get(row.specialty);
    if (!winner) return false;
    if (row.isDefault) return winner.isDefault;
    return !winner.isDefault && winner.order === row.order;
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 py-16 text-sm text-gray-400">
          Loading workflows...
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 py-16 text-sm text-red-500">
          {loadError}
        </div>
      );
    }
    if (!selectedProvider) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-6 py-16 text-center">
          <Users className="h-8 w-8 text-gray-300" />
          {/* An id no route names means a stale link, which is worth saying
              rather than showing an empty picker. */}
          {doctorId && (
            <p className="mt-3 max-w-md break-words text-sm font-semibold text-red-500">
              No doctor in the workflow config has the id "{doctorId}".
            </p>
          )}
          <p className="mt-3 text-sm font-semibold text-gray-700">
            Select a doctor to see their routes
          </p>
          <p className="mt-1 max-w-md text-sm text-gray-500">
            Search by name to see every route that applies to them, listed in the
            order the backend evaluates them.
          </p>
          <button
            type="button"
            onClick={openProviderSearch}
            className="mt-5 cursor-pointer rounded-md bg-ktx-purple px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ktx-purple/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ktx-purple focus-visible:ring-offset-2"
          >
            Search doctors
          </button>
        </div>
      );
    }
    if (!selectedSpecialty) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-6 py-16 text-center">
          <Layers className="h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm font-semibold text-gray-700">
            Pick a specialty to see these routes
          </p>
          <p className="mt-1 max-w-md text-sm text-gray-500">
            {selectedProviderEntry?.name} has routes in{" "}
            {specialtyOptions.length} specialties, and each one is evaluated on
            its own. Choose which specialty you're asking about.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {specialtyOptions.map((specialty) => (
              <button
                key={specialty}
                type="button"
                onClick={() => onSelectSpecialty?.(specialty)}
                className="cursor-pointer rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-ktx-purple hover:text-ktx-purple"
              >
                {specialty}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 py-16 text-sm text-gray-400">
          No routes found for {selectedProviderEntry?.name || "this doctor"} in{" "}
          {selectedSpecialty}.
        </div>
      );
    }
    // No flex-1: the card hugs its rows and only scrolls once they outgrow the
    // available height.
    return (
      <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* The header is its own table so the scrollbar belongs to the rows
            alone, and it reserves that scrollbar's width to stay aligned. */}
        <div className="shrink-0" style={{ paddingRight: scrollbarWidth }}>
          <table className="w-full table-fixed">
            {renderColumns()}
            <thead className="bg-gray-50">
              <tr>
                {["Path", "Doctors", "Visit type"].map((label) => (
                  <th key={label} className={HEAD_CELL}>
                    {label}
                  </th>
                ))}
                {showFlagsColumn && (
                  <th className={`${HEAD_CELL} ${FLAG_GUTTER}`}>Flags</th>
                )}
                <th className={`${HEAD_CELL} ${RESOURCE_GUTTER}`}>
                  <div className="flex items-center justify-between gap-3">
                    Resources
                    {/* Easy to lose track of for a multi-specialty doctor. */}
                    <Badge className="flex items-center gap-1.5 border-gray-300 bg-white normal-case tracking-normal text-gray-600">
                      <Layers className="h-3.5 w-3.5" />
                      {selectedSpecialty}
                    </Badge>
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>
        <div ref={bodyRef} className="min-h-0 overflow-y-auto">
          {/* Fixed layout so long template ids wrap in place instead of
              stretching the table into a horizontal scroll. */}
          <table className="w-full table-fixed">
            {renderColumns()}
            <tbody>
              {rows.map((row) => {
                const matched = isRowMatched(row);
                return (
                  <tr
                    key={row.key}
                    className={`border-b border-gray-100 transition-colors ${
                      // The rule above the fallback separates it from the
                      // numbered routes, matched or not.
                      row.isDefault ? "border-t border-gray-200" : ""
                    } ${
                      matched
                        ? "bg-emerald-50"
                        : row.isDefault
                          ? "bg-violet-50/60"
                          : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-3 py-3 align-top">
                      {/* The matched marker stacks under the row identity;
                          beside it, it overflows this fixed-width cell. */}
                      <div className="flex flex-col items-start gap-1">
                        <Badge
                          variant="outline"
                          className={`rounded-md border-transparent px-2 py-0.5 text-xs font-bold ${
                            matched
                              ? "bg-emerald-200 text-emerald-900"
                              : row.isDefault
                                ? "bg-violet-100 text-violet-800"
                                : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {/* `order` is 0-based in workflows.json, but "Path 0"
                              reads as a missing value rather than the first. */}
                          {row.isDefault ? "FALLBACK" : `Path ${row.order + 1}`}
                        </Badge>
                        {matched && (
                          <Badge
                            variant="outline"
                            className="rounded border-emerald-300 bg-white px-1.5 py-0 text-xs font-semibold text-emerald-700"
                          >
                            Matched
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <DoctorsCell
                        providers={row.providers}
                        isDefault={row.isDefault}
                        selectedProviderName={selectedProviderEntry?.name || null}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <VisitTypeCell types={row.types} isDefault={row.isDefault} />
                    </td>
                    {showFlagsColumn && (
                      <td className={`px-4 py-3 align-top ${FLAG_GUTTER}`}>
                        <FlagsCell flags={row.flags} isDefault={row.isDefault} />
                      </td>
                    )}
                    <td className={`px-4 py-3 align-top ${RESOURCE_GUTTER}`}>
                      <ResourcesCell resources={row.resources} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="mb-3 flex flex-wrap items-center gap-4">
        {/* Doctor search-and-select */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Doctor:</label>
          <div className="relative" ref={providerRef}>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={providerInputRef}
              className="h-10 w-64 rounded-md border border-gray-300 bg-white pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ktx-purple"
              placeholder="Search doctor..."
              value={
                providerOpen ? providerSearch : selectedProviderEntry?.name || ""
              }
              onFocus={() => {
                setProviderOpen(true);
                setProviderSearch("");
              }}
              onChange={(e) => {
                setProviderOpen(true);
                setProviderSearch(e.target.value);
              }}
            />
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            {providerOpen && (
              <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-md border border-gray-200 bg-white shadow-lg">
                <div className="max-h-72 space-y-0.5 overflow-y-auto p-1">
                  {filteredProviders.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleSelectProvider(p)}
                      className={`block w-full cursor-pointer truncate rounded-md px-3 py-2 text-left text-sm transition-colors ${
                        p.id === selectedProvider
                          ? "bg-ktx-purple font-semibold text-white"
                          : "text-gray-700 hover:bg-purple-50"
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                  {filteredProviders.length === 0 && (
                    <p className="px-2 py-4 text-center text-sm text-gray-400">
                      No doctors match.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Multi-specialty doctors must pick one; there is no "all" option. */}
        {needsSpecialtyChoice && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Specialty:
            </label>
            {/* Radix reserves "" for a controlled Select showing its
                placeholder. */}
            <Select
              value={selectedSpecialty || ""}
              onValueChange={onSelectSpecialty}
            >
              <SelectTrigger
                className={`h-10 w-56 text-sm focus:border-ktx-purple focus:ring-2 focus:ring-ktx-purple focus:ring-offset-0 ${
                  selectedSpecialty
                    ? "border-gray-300"
                    : "border-ktx-purple ring-2 ring-ktx-purple/20"
                }`}
              >
                <SelectValue placeholder="Required — pick one" />
              </SelectTrigger>
              <SelectContent>
                {specialtyOptions.map((specialty) => (
                  <SelectItem key={specialty} value={specialty}>
                    {specialty}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Conditions only matter once a doctor and specialty are settled. */}
        {selectedProvider && selectedSpecialty && (
          <>
            {/* Offered even where no route is typed, since "Any" is still
                worth asking there. */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Visit type:
              </label>
              <Select
                value={selectedVisitType || NO_VISIT_TYPE}
                onValueChange={(value) =>
                  setSelectedVisitType(value === NO_VISIT_TYPE ? null : value)
                }
              >
                <SelectTrigger className="h-10 w-48 border-gray-300 text-sm focus:border-ktx-purple focus:ring-2 focus:ring-ktx-purple focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_VISIT_TYPE} className="text-gray-500">
                    None selected
                  </SelectItem>
                  <SelectItem value={ANY_VISIT_TYPE}>Any visit type</SelectItem>
                  {visitTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {flags.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Flags:
                </label>
                <div className="relative" ref={flagsRef}>
                  <button
                    type="button"
                    onClick={() => setFlagsOpen((o) => !o)}
                    className="flex h-10 w-52 cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple"
                  >
                    <span className="flex items-center gap-2 truncate">
                      <Flag className="h-4 w-4 shrink-0 text-orange-500" />
                      <span
                        className={
                          selectedFlags.size ? "text-gray-900" : "text-gray-400"
                        }
                      >
                        {selectedFlags.size
                          ? `${selectedFlags.size} selected`
                          : "None selected"}
                      </span>
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                  </button>
                  {flagsOpen && (
                    <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg">
                      <div className="max-h-72 space-y-0.5 overflow-y-auto p-1">
                        {flags.map((flag) => {
                          const on = selectedFlags.has(flag);
                          return (
                            <button
                              key={flag}
                              type="button"
                              onClick={() => toggleFlag(flag)}
                              className={`flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                                on
                                  ? "bg-orange-50 font-semibold text-orange-800"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                  on
                                    ? "border-orange-500 bg-orange-500 text-white"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {on && (
                                  <svg
                                    viewBox="0 0 12 12"
                                    className="h-3 w-3"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path
                                      d="M2.5 6.5l2.5 2.5 4.5-5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </span>
                              <span className="truncate">{flag}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {conditionsActive && (
              <button
                type="button"
                onClick={clearConditions}
                className="flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear conditions
              </button>
            )}
          </>
        )}
      </div>

      {renderBody()}

      {selectedProvider && !loading && !loadError && rows.length > 0 && (
        <div className="mt-3 shrink-0">
          <p className="text-sm leading-relaxed text-gray-500">
            <span className="font-semibold text-gray-600">
              How routes are picked:
            </span>{" "}
            Routes are checked in order and the first one whose conditions all
            pass wins. A condition left blank means "no restriction", so a route
            with no visit type applies to every visit type. Flag conditions
            require every listed flag to be true for that route. If no route
            passes, the FALLBACK route applies.
          </p>
        </div>
      )}
    </div>
  );
}

export default TableView;
